import z from 'zod';
import { paramValueSchema, territorialIdSchema } from './paramTypeSchema.js';
import { timestampSchema } from './commonSchemas.js';
/**
 * Schema per TerritorialInfo
 * Rappresenta le informazioni territoriali espanse dalla funzione get_territorial_by_ids()
 */
export const territorialInfoSchema = z.object({
    /** Latitudine del territorio */
    lat: z.string().regex(/^-?\d+(\.\d+)?$/, "La latitudine deve essere un numero float in formato stringa").nullable(),
    /** Longitudine del territorio */
    lon: z.string().regex(/^-?\d+(\.\d+)?$/, "La longitudine deve essere un numero float in formato stringa").nullable(),
    /** Nome del territorio */
    name: z.string().nullable(),
    /** Tipo di territorio: provincia, comune, etc. */
    type: z.enum(['provincia', 'comune', 'regione', 'nazione']),
    /** Sigla della provincia */
    sigla: z.string().nullable(),
    /** Livello numerico nella gerarchia territoriale (1=nazione, 2=regione, 3=provincia, 4=comune) */
    level_num: z.number().int().min(0).max(4).nullable(),
    /** ID del territorio padre */
    parent_id: z.string().nullable(),
    /** ID della regione */
    region_id: z.string().nullable(),
    /** ID della provincia */
    province_id: z.string().nullable(),
    /** Nome della regione */
    region_name: z.string().nullable(),
    /** Nome della provincia */
    province_name: z.string().nullable(),
    /** Flag capoluogo: SI/NO */
    flag_capoluogo: z.enum(['SI', 'NO']).nullable(),
    /** Sigla della provincia */
    province_sigla: z.string().nullable(),
    /** ID territoriale completo */
    territorial_id: z.string().nullable(),
    /** Codice Belfiore del comune */
    codice_belfiore: z.string().nullable(),
    /** Codice territoriale */
    territorial_code: z.string().nullable(),
});
/**
 * Schema per BonusParam
 * Rappresenta un parametro concreto associabile al bonus
 */
export const bonusParamSchema = z.object({
    /** ID del parametro da cui può ricavare il tipo, il nome ecc... */
    parameter_id: z.number().int().positive(),
    /** Valore del parametro, può essere di qualsiasi tipo */
    value: paramValueSchema
});
/**
 * Schema per Links esterni del bonus
 * Oggetto JSONB con chiavi string e valori URL
 */
export const bonusLinksSchema = z.record(z.string(), z.string()).optional().nullable();
/**
 * Schema per Files allegati del bonus
 * Array JSONB con id dei file archiviati
 */
export const bonusFilesSchema = z.array(z.number()
    .int()
    .positive()).optional().nullable();
// Schema per date che accetta sia string che Date
const dateSchema = z.union([
    z.string().regex(/^\d{4}-\d{2}-\d{2}/, "La data deve essere nel formato YYYY-MM-DD"),
    z.string().datetime("La data deve essere in formato ISO"),
    z.date()
]).optional();
/**
 * Schema per categories (array di category_id)
 */
export const bonusCategoriesSchema = z.array(z.number().int().nonnegative());
/**
 * Schema per eroganti (array di category_id)
 */
export const bonusErogantiSchema = z.array(z.number().int().nonnegative());
/**
 * Schema per BonusDefRow
 * Corrisponde alla struttura della tabella 'bonus'
 * Rappresenta la riga della tabella dei bonus pensionati
 * @db_table bonus
 */
export const bonusDefRowSchema = z.object({
    /** ID del bonus */
    id: z.number().int("Il numero deve essere un intero").positive("Il numero deve essere>0").describe('type:number,editable:false,label:ID'),
    /** Array di territoriali (jsonb array) */
    ambito_territoriale: z.array(territorialIdSchema).describe('type:ambito,label:Ambito Territoriale'),
    /** Array di category_id per classificazione tematica */
    categories: bonusCategoriesSchema.describe('type:categories,label:Categorie'),
    /** Array di eroganti_id per identificare gli eroganti */
    eroganti: bonusErogantiSchema.describe('type:eroganti,label:Enti eroganti'),
    /** ID dell'organizzazione proprietaria del bonus (0 per 'tutti') */
    organization_id: z.number().int().min(0).describe('type:organization,label:Organizzazione'),
    /** Nome del bonus */
    title: z.string().min(1, "Il titolo del bonus è obbligatorio").describe('label:Titolo'),
    /** Descrizione del bonus */
    description: z.string().optional().nullable().describe('type:textarea,label:Descrizione'),
    /** Data di inizio */
    data_inizio: dateSchema.nullable().describe('type:date,label:Data Inizio'),
    /** Data di fine */
    data_fine: dateSchema.nullable().describe('type:date,label:Data Fine'),
    /** Parametri associati al bonus - supporta grouping per logica OR/AND */
    parameters: z.union([
        z.array(bonusParamSchema), // Caso semplice: [{}, {}] - tutti in AND
        z.array(z.array(bonusParamSchema)) // Caso gruppi: [[{},{}], [{},{}]] - gruppi in OR, elementi del gruppo in AND
    ]).optional()
        .describe('type:bonusparam,label:Parametri'),
    /** Links esterni (siti ufficiali, normative, guide) */
    links: bonusLinksSchema.describe('type:clinks,label:Links Esterni'),
    /** Files allegati (moduli, documentazione) */
    files: bonusFilesSchema.describe('type:files,label:Files Allegati'),
    excerpt: z.string().optional().nullable().describe('type:textarea,label:Estratto,help:Breve estratto'),
    /** Se il bonus è pubblicato o meno */
    published: z.boolean().optional().describe('label:Pubblicato,defaultValue:true').default(true),
    /** Data di creazione del bonus */
    created_at: timestampSchema.describe('type:datetime,editable:false,label:Data Creazione'),
    /** Data di ultima modifica del bonus */
    updated_at: timestampSchema.describe('type:datetime,editable:false,label:Data Modifica')
});
export const bonusDefRowSchemaNoId = bonusDefRowSchema.omit({ id: true, created_at: true, updated_at: true });
/**
 * Schema per BonusDefRowView
 * @db_view bonus_list_view
 */
/**
 * Schema per MatchDetail
 * Rappresenta i dettagli del matching di un parametro specifico
 */
export const matchDetailSchema = z.object({
    /** ID del parametro */
    parameter_id: z.number().int().positive().optional().nullable(),
    /** Valore del parametro nel bonus */
    parameter_value: paramValueSchema.optional().nullable(),
    /** Nome del parametro */
    parameter_name: z.string().optional().nullable(),
    /** Tipo di parametro (min, max, eq, in, etc.) */
    parameter_type_name: z.string().optional().nullable(),
    /** Tipo di valore atteso dal parametro */
    parameter_type_value: z.string().optional().nullable(),
    /** ID dell'attributo collegato */
    attribute_id: z.number().int().positive().optional().nullable(),
    /** Valore dell'attributo nel profilo */
    profile_attribute_value: paramValueSchema.optional().nullable(),
    /** Risultato del matching: true=match, false=no match, null=non valutabile */
    matches: z.boolean().nullable().optional(),
    /** Indica se l'attributo è presente nel profilo */
    has_attribute: z.boolean().optional().nullable()
});
export const bonusDefRowSchemaView = bonusDefRowSchema.extend({
    /** Informazioni territoriali espanse tramite get_territorial_by_ids() */
    ambito: z.array(territorialInfoSchema).optional().nullable(),
    /** Oggetti categorie completi */
    categories_details: z.array(z.any()).optional(),
    /** Oggetti eroganti completi */
    eroganti_details: z.array(z.any()).optional(),
    /** Numero categorie */
    categories_count: z.number().int().nonnegative().optional(),
    /** Indica se il bonus è permanente (data_fine IS NULL) */
    is_permanent: z.boolean(),
    /** Indica se il bonus è attivo (pubblicato e nel periodo di validità) */
    is_active: z.boolean(),
    /** Indica se il bonus è scaduto */
    is_expired: z.boolean(),
    parameters_expanded: z.any().optional().nullable(),
    /** Numero di parametri associati al bonus */
    parameters_count: z.number(),
    /** Numero di ambiti territoriali coperti dal bonus */
    territories_count: z.number(),
    /** Status del bonus: draft|active|expired|scheduled */
    status: z.enum(['draft', 'active', 'expired', 'scheduled']),
    /** Giorni rimanenti alla scadenza, NULL se permanente */
    days_to_expiry: z.number().nullable(),
    published: z.boolean().optional()
});
/**
 * Schema per BonusDefRowWithMatch
 * Estende bonusDefRowSchemaView con i dettagli del matching
 */
export const bonusDefRowSchemaViewWithMatchSchema = bonusDefRowSchemaView.extend({
    /** Dettagli del matching dei parametri */
    match_details: z.any().optional().nullable(),
    published: z.boolean().optional()
});
//# sourceMappingURL=bonusDefSchema.js.map